"use client";

import { ChevronRight, Sparkles } from "lucide-react";
import type { ProfileMagicReadiness } from "@/lib/profileMagicReadiness";
import { ProfileMagicBillinMascot } from "@/app/components/profile-magic/ProfileMagicBillinMascot";
import { AeraButton } from "@/app/components/ui/AeraButton";

function SkeletonBlock({ className }: { className: string }) {
  return (
    <div
      className={["animate-pulse rounded-md bg-aera-lightgray/50", className].join(
        " ",
      )}
      aria-hidden
    />
  );
}

export function ProfileMagicProgressCardSkeleton() {
  return (
    <div
      className="w-full xl:row-span-2 xl:col-start-2 rounded-2xl border border-aera-amberlight bg-aera-amberlight/10 p-4"
      aria-busy="true"
      aria-label="กำลังโหลด"
    >
      <div className="min-w-0 flex flex-col gap-2">
        <div className="flex items-center gap-4">
          <div className="w-full flex flex-col gap-2 md:gap-3">
            <div className="flex flex-wrap items-center gap-2 md:gap-3">
              <SkeletonBlock className="h-5 w-48" />
              <SkeletonBlock className="h-5 w-14 rounded-full" />
            </div>

            <div className="flex items-center justify-between gap-3">
              <SkeletonBlock className="h-4 w-32" />
              <SkeletonBlock className="h-4 w-8" />
            </div>

            <SkeletonBlock className="h-2.5 w-full rounded-full" />

            <SkeletonBlock className="h-3 w-full max-w-sm" />
          </div>

          <SkeletonBlock className="aspect-square w-full max-w-[100px] shrink-0 rounded-2xl md:max-w-[80px]" />
        </div>

        <SkeletonBlock className="h-10 w-full rounded-xl" />
      </div>
    </div>
  );
}

type Props = {
  readiness: ProfileMagicReadiness;
  onContinue: () => void;
};

export function ProfileMagicProgressCard({ readiness, onContinue }: Props) {
  if (readiness.percent >= 100) return null;

  const percent = Math.max(0, Math.min(100, readiness.percent));

  return (
    <div className="w-full xl:row-span-2 xl:col-start-2 rounded-2xl border border-aera-amberlight bg-aera-amberlight/10 p-4">
      <div className="min-w-0 flex flex-col gap-2">
        <div className="flex items-center gap-4">
          <div className="w-full flex flex-col gap-2 md:gap-3">
            <div className="flex flex-wrap items-center gap-2 md:gap-3">
              <span className="min-w-0 text-sm font-extrabold text-aera-charcoal flex items-center gap-1.5">
                <Sparkles className="h-5 w-5 text-aera-amber" />
                ทำให้บิลลินจับคู่แม่นขึ้น
              </span>
              <span className="shrink-0 rounded-full border border-aera-amberlight bg-aera-white px-2 pt-0.5 text-[11px] font-bold text-aera-charcoal">
                แนะนำ
              </span>
            </div>

            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-semibold text-aera-charcoal/80">
                ความพร้อมในการจับคู่
              </p>
              <span className="text-sm font-black text-aera-charcoal tabular-nums">
                {percent}%
              </span>
            </div>

            <div className="h-2.5 overflow-hidden rounded-full bg-aera-lightgray/80">
              <div
                className="h-full rounded-full bg-linear-to-r from-aera-amberlight to-aera-amber"
                style={{ width: `${percent}%` }}
              />
            </div>

            <p className="text-xs font-medium text-aera-charcoal/60">
              เพิ่มข้อมูลบิลลิน เพื่อช่วยให้การจับคู่ทำงานได้ดีขึ้น
            </p>
          </div>

          <div className="flex justify-center">
            <ProfileMagicBillinMascot size="sm" variant="b7"/>
          </div>
        </div>

        <AeraButton
          type="button"
          onClick={onContinue}
          variant="secondary"
          fontWeight="medium"
          fullWidth
          iconRight={<ChevronRight className="h-4 w-4" />}
        >
          ทำต่อ 1 นาที
        </AeraButton>
      </div>
    </div>
  );
}
